import { logSwallowed } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { createSDK, type ZDCSDK } from '@zdc/sdk';
import { useCenterStore } from './centerStore';
import { useAppModeStore } from './appModeStore';
import { isWailsMode, setProxyTarget, getLoopbackUrl, onWailsConfigChange } from '@/lib/wailsBridge';
import { parseAccessGateError, useAccessGateStore } from '@/lib/accessGate';

const sdkLog = createLogger('sdkStore');

/**
 * Desktop (Wails): SDK ходит на ЛОКАЛЬНЫЙ loopback-прокси ЭТОГО экземпляра
 * (полноценный net/http, стримы с flush). Адрес — НЕ хардкод: он приходит из
 * Go-конфига (App.GetConfig().loopback_url) через wailsBridge.getLoopbackUrl()
 * — порт свой на инстанс (выдаёт ОС при старте процесса; мульти-инстанс).
 * Конфиг читается ДО первого рендера (main.tsx) — к моменту создания SDK
 * фактический адрес уже известен (getLoopbackUrl() без конфига бросает).
 * ПОЧЕМУ НЕ wails.localhost: AssetServer Wails буферизует тело ответа до
 * Finish() (Flush недоступен) — длинные стримы не доходят. См.
 * desktop/HydraDatacentreDesktop/main.go.
 *
 * При смене loopback-адреса инстанса (новый конфиг) SDK пересоздаётся —
 * ниже getSDK() сравнивает URL, а подписка форсирует сброс.
 */
onWailsConfigChange(() => {
  if (isWailsMode()) resetSDK();
});

function logToStorage(msg: string) {
  try {
    const key = 'hydra_sdk_log';
    const existing = localStorage.getItem(key) || '';
    const entry = `${new Date().toISOString()} ${msg}\n`;
    const updated = (existing + entry).slice(-5000);
    localStorage.setItem(key, updated);
  } catch (e) { logSwallowed(e, 'store/sdkStore.ts:34'); }
}

let sdkInstance: ZDCSDK | null = null;
let sdkUrl = '';

// Флаг: ошибки журнала регистрации НЕ логируем повторно (предотвращает рекурсию
// eventLog → SDK → onRpcError → eventLog при сбое самого журнала).
const rpcErrorDedup = new Map<string, number>();
const RPC_ERROR_DEDUP_MS = 10000;

function rpcErrorToJournal(method: string, error: unknown): void {
  // 412 гейтинга доступа (access_gating): показать баннер «Подтвердите номер».
  // Это НЕ ошибка для журнала — ожидаемое состояние политики базы.
  try {
    const notice = parseAccessGateError(error);
    if (notice) {
      useAccessGateStore.getState().mark(notice);
      return;
    }
  } catch { /* ниже обычная обработка */ }
  if (method.includes('/EventLogService/')) return;
  // Тихие NotFound для методов-ЧТЕНИЙ (нормальное отсутствие, не ошибка):
  // аватар без файла, обои/стол не задан, файл удалён, чат недоступен.
  // Не мусорим журнал («куча ошибок» у пользователя) — остальное в журнал.
  try {
    const anyE = error as { code?: unknown };
    const isNotFound = anyE?.code === 5 || String(anyE?.code).endsWith('NotFound') || String(anyE?.code).toLowerCase().includes('not_found');
    if (isNotFound && ['DownloadFile', 'GetChatWallpaper', 'GetConversation', 'GetFileThumb'].some((m) => method.includes(m))) {
      return;
    }
  } catch { /* определение ошибки ниже */ }
  try {
    void import('@/lib/eventLog').then(({ eventLog }) => {
      const msg = error instanceof Error ? error.message : String(error || '');
      const anyE = error as Record<string, unknown>;
      const code = String(anyE['code'] ?? '');
      const raw = typeof anyE['rawMessage'] === 'string' ? String(anyE['rawMessage']) : '';
      const stack = error instanceof Error && error.stack ? error.stack.slice(0, 2000) : '';
      // Сквозной X-Request-Id (B4): ConnectError.metadata несёт заголовок
      // ответа (Expose-Headers) — пишем в журнал для сквозной трассировки.
      const meta = anyE['metadata'] as { get?: (k: string) => string | null } | undefined;
      const requestId = meta && typeof meta.get === 'function'
        ? String(meta.get('x-request-id') || meta.get('X-Request-Id') || '')
        : '';
      // Анти-лавина: одну и ту же ошибку (метод+код) в журнал регистрации
      // пишем раз в 10с; console/ring-лог — ВСЕГДА (подробно, для разбора).
      const key = `${method}:${code}`;
      const now = Date.now();
      const last = rpcErrorDedup.get(key) || 0;
      const { centerUrl, baseSlug } = useCenterStore.getState();
      const ctx = `method=${method} code=${code} center=${centerUrl || '(local)'} base=${baseSlug || '(none)'} request_id=${requestId || '—'}`;
      console.warn(`[rpc] ${ctx} — ${msg}${raw ? ` | ${raw}` : ''}`);
      if (now - last >= RPC_ERROR_DEDUP_MS) {
        rpcErrorDedup.set(key, now);
        const ctxDetails = [ctx, raw].filter(Boolean).join(' | ');
        void eventLog.error('ui', `RPC ${method}: ${msg}`, { details: [ctxDetails, stack].filter(Boolean).join(' | ') || undefined });
      }
    });
  } catch (e) { logSwallowed(e, 'store/sdkStore.ts:84'); }
}

export function getSDK(): ZDCSDK {
  const { centerUrl, token, baseSlug } = useCenterStore.getState();

  // Desktop (Wails): SDK ходит на ЛОКАЛЬНЫЙ LOOPBACK-ПРОКСИ ЭТОГО экземпляра
  // (фактический адрес — из конфига Go, getLoopbackUrl(); порт свой на инстанс).
  // ПОЧЕМУ НЕ wails.localhost: Wails AssetServer буферизует тело ответа
  // до Finish() (Flush недоступен) — длинные стримы (EventBus Subscribe,
  // _ping каждые 15с) не доставлялись, watchdog рвал соединение каждые 45с.
  // 127.0.0.1 — secure context (mixed content не блокируется), CORS даёт
  // сам loopback-сервер (main.go). Прокси по-прежнему на активный центр
  // (setProxyTarget: '' = локальный сервер).
  // Web/PWA: SDK ходит напрямую на centerUrl (или свой origin).
  const isDesktop = isWailsMode();
  const url = isDesktop ? getLoopbackUrl() : (centerUrl || window.location.origin);

  // Переключаем прокси десктопа на активный центр ('' = локальный сервер).
  if (isDesktop) {
    setProxyTarget(centerUrl || '');
  }

  // SDK создаётся под конкретный URL. Если URL сменился — пересоздаём.
  if (!sdkInstance || sdkUrl !== url) {
    sdkInstance = createSDK({
      baseUrl: url,
      // Активная база на МОМЕНТ создания: X-Hydra-Base уходит с ПЕРВОГО
      // запроса (не только после setBase ниже) — заголовок обязателен ВСЕГДА
      // для /zdc.* (включая ExternalApiService.TranscribeAudio).
      base: baseSlug || undefined,
      // База читается В МОМЕНТ каждого запроса (устраняет гонку гидратации:
      // SDK, созданный до восстановления baseSlug, слал запросы без заголовка
      // → 400 «no base selected» → пустые списки/формы).
      baseResolver: () => useCenterStore.getState().baseSlug || '',
      // ВСЕ ошибки RPC (кроме стримов) → журнал регистрации (категория ui).
      onRpcError: (method, error) => rpcErrorToJournal(method, error),
    });
    sdkUrl = url;
    // Desktop-клиент: X-Hydra-Client: desktop (сервер распознаёт desktop
    // без依赖 от UA — WebView2 не содержит "wails" в User-Agent).
    sdkInstance.setDesktopClient(isDesktop);
    console.log(`[zdc-sdk] new SDK baseUrl="${url}" isDesktop=${isDesktop} center="${centerUrl || '(local)'}" base="${baseSlug || ''}"`);
    logToStorage(`SDK created baseUrl="${url}" isDesktop=${isDesktop} center="${centerUrl || '(local)'}" base="${baseSlug || ''}"`);
  }

  if (token) {
    sdkInstance.setToken(token);
  }

  // Активная база (базы-как-схемы): заголовок X-Hydra-Base на все запросы
  // (включая стримы EventBus). Применяется при КАЖДОМ обращении — база
  // могла смениться (setBase в centerStore).
  sdkInstance.setBase(baseSlug || null);

  // Режим интерфейса (как в 1С): Конфигуратор видит ВСЕ объекты,
  // Предприятие — по функциональным опциям (сервер фильтрует по X-Hydra-Mode)
  sdkInstance.setMode(useAppModeStore.getState().mode);

  return sdkInstance;
}

export function resetSDK(): void {
  sdkInstance = null;
  sdkUrl = '';
}
